import { useState } from 'react';
import { Ellipsis } from 'lucide-react';
import { PencilLineIcon } from '@/components/icons/pencil-line-icon';
import { TrashIcon } from '@/components/icons/trash-icon';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { TableCell, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { DeleteDialog } from '@/components/shared/delete-dialog';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { useSetResponseStrategyActive, useDeleteResponseStrategy } from '@/hooks/use-response-strategies';
import {
	useUpdateResponseStrategyTemplateEditableFields,
	useDeleteResponseStrategyTemplate
} from '@/hooks/use-response-strategy-templates';
import { usePermissions } from '@/hooks/use-permissions';
import { toast } from '@/components/ui/sonner';
import type { ResponseStrategy } from '@/types';

const VISIBLE_INTENT_LIMIT = 2;

const INTENT_TAG_CLASS = 'bg-gray-100 text-gray-800 border-transparent text-xs font-normal rounded-full';

interface ResponseStrategyRowProps {
	strategy: ResponseStrategy;
	onClick?: (strategy: ResponseStrategy) => void;
}

export function ResponseStrategyRow({ strategy, onClick }: ResponseStrategyRowProps) {
	const { isSuperAdmin } = usePermissions();
	const setStrategyActiveMutation = useSetResponseStrategyActive();
	const updateTemplateFieldsMutation = useUpdateResponseStrategyTemplateEditableFields();
	const deleteMutation = useDeleteResponseStrategy();
	const deleteTemplateMutation = useDeleteResponseStrategyTemplate();
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [enableConfirmOpen, setEnableConfirmOpen] = useState(false);
	const [enableDraftConfirmOpen, setEnableDraftConfirmOpen] = useState(false);
	const [intentsOpen, setIntentsOpen] = useState(false);

	const isActive = strategy.isActive;
	// Enable/disable only needs the regular edit permission for templates too —
	// only editing intents/content or deleting is super-admin only.
	const setActiveMutation = strategy.isTemplate ? updateTemplateFieldsMutation : setStrategyActiveMutation;
	const visibleIntents = strategy.intents.slice(0, VISIBLE_INTENT_LIMIT);
	const overflowCount = strategy.intents.length - visibleIntents.length;

	const setActive = async (nextIsActive: boolean) => {
		try {
			if (strategy.isTemplate) {
				await updateTemplateFieldsMutation.mutateAsync({ id: strategy.id, data: { isActive: nextIsActive } });
			} else {
				await setStrategyActiveMutation.mutateAsync({ id: strategy.id, isActive: nextIsActive });
			}
		} catch {
			toast.error('Failed to update response strategy status');
		}
	};

	const handleToggleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (isActive) {
			setActive(false);
		} else if (strategy.isDraft) {
			setEnableDraftConfirmOpen(true);
		} else {
			setEnableConfirmOpen(true);
		}
	};

	const handleDelete = async () => {
		try {
			if (strategy.isTemplate) {
				await deleteTemplateMutation.mutateAsync(strategy.id);
			} else {
				await deleteMutation.mutateAsync(strategy.id);
			}
			toast.success('Response strategy deleted');
			setDeleteOpen(false);
		} catch {
			toast.error('Failed to delete response strategy');
		}
	};

	// Deleting a template (unlike enable/disable or intent mapping) is
	// super-admin only server-side — hide the menu item rather than let it 403.
	// A template-derived override (generatedBy: "template:<id>") can never be
	// deleted, by anyone — it's this account's link to the shared template;
	// disabling it is the only supported way to turn it off (server rejects
	// the delete outright, so this just avoids showing a control that 400s).
	const isTemplateOverride = strategy.generatedBy?.startsWith('template:') ?? false;
	const canDelete = strategy.isTemplate ? isSuperAdmin : !isTemplateOverride;

	return (
		<>
			<TableRow>
				{/* Name */}
				<TableCell className='w-2/5'>
					<span className='text-[13px] leading-[18px] text-ink'>{strategy.name}</span>
				</TableCell>

				{/* Intent */}
				<TableCell>
					<div className='flex items-center gap-2 flex-wrap'>
						{visibleIntents.map((intent) => (
							<Badge key={intent} variant='secondary' className={INTENT_TAG_CLASS}>
								{intent}
							</Badge>
						))}
						{overflowCount > 0 && (
							<Popover open={intentsOpen} onOpenChange={setIntentsOpen}>
								<PopoverTrigger asChild>
									<Badge
										variant='secondary'
										className={INTENT_TAG_CLASS}
										onMouseEnter={() => setIntentsOpen(true)}
										onMouseLeave={() => setIntentsOpen(false)}
									>
										+{overflowCount}
									</Badge>
								</PopoverTrigger>
								<PopoverContent
									align='start'
									onMouseEnter={() => setIntentsOpen(true)}
									onMouseLeave={() => setIntentsOpen(false)}
									className='w-auto p-2 flex flex-col gap-2 bg-white/80 backdrop-blur-[6px] border-border/60 shadow-[0px_3px_12px_0px_rgba(0,0,0,0.08)] rounded-xl'
								>
									{strategy.intents.slice(VISIBLE_INTENT_LIMIT).map((intent) => (
										<Badge key={intent} variant='secondary' className={INTENT_TAG_CLASS}>
											{intent}
										</Badge>
									))}
								</PopoverContent>
							</Popover>
						)}
					</div>
				</TableCell>

				{/* Active */}
				<TableCell onClick={(e) => e.stopPropagation()}>
					<div className='flex items-center justify-end gap-3'>
						<div className='flex w-16 justify-end'>
							{strategy.isDraft && (
								<Badge variant='info' className='font-normal bg-blue-100 border-transparent'>
									Draft
								</Badge>
							)}
						</div>
						<span className='h-4 w-px shrink-0 bg-gray-300' />
						<div className='flex items-center gap-[51px]'>
							<Switch
								checked={isActive}
								onCheckedChange={() => {}}
								onClick={handleToggleClick}
								disabled={setActiveMutation.isPending}
								className='cursor-pointer'
							/>

							<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant='ghost'
									size='icon'
									className='h-7 w-7 text-gray-800'
									onClick={(e) => e.stopPropagation()}
								>
									<Ellipsis size={14} />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								align='end'
								className='bg-white/95 backdrop-blur-[6px] border-gray-300 shadow-[0px_3px_12px_0px_rgba(0,0,0,0.08)] rounded-2xl p-1.5 [&>*+*]:mt-1.5'
							>
								<DropdownMenuItem
									onSelect={() => onClick?.(strategy)}
									className='rounded-[10px] pl-2.5 pr-1.5 py-1 text-[13px] text-ink focus:text-ink'
								>
									<PencilLineIcon className='size-4 text-current' />
									Edit
								</DropdownMenuItem>
								{canDelete && (
									<DropdownMenuItem
										onSelect={() => setDeleteOpen(true)}
										className='rounded-[10px] pl-2.5 pr-1.5 py-1 text-[13px] text-red-500 focus:text-red-500'
									>
										<TrashIcon className='size-4 text-current' />
										Delete
									</DropdownMenuItem>
								)}
							</DropdownMenuContent>
						</DropdownMenu>
						</div>
					</div>
				</TableCell>
			</TableRow>

			<DeleteDialog
				open={deleteOpen}
				onOpenChange={setDeleteOpen}
				onConfirm={handleDelete}
				title='Delete strategy?'
				description='Deleting this response strategy is permanent. Do you want to continue?'
				isLoading={strategy.isTemplate ? deleteTemplateMutation.isPending : deleteMutation.isPending}
			/>

			<AlertDialog open={enableConfirmOpen} onOpenChange={setEnableConfirmOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Enable strategy?</AlertDialogTitle>
						<AlertDialogDescription>
							Once enabled, this particular response strategy will fire whenever the intents are triggered
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								setEnableConfirmOpen(false);
								setActive(true);
							}}
						>
							Enable
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog open={enableDraftConfirmOpen} onOpenChange={setEnableDraftConfirmOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Enable strategy?</AlertDialogTitle>
						<AlertDialogDescription>This strategy is still a draft. Save it and turn it on</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								setEnableDraftConfirmOpen(false);
								setActive(true);
							}}
						>
							Save and Enable
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
